import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  listTenantDefaultVolumeType,
  listVolumeTypes,
} from 'app/plugins/openstack/components/storage/volume-types/actions'
import {
  defaultTenantVolumeTypesSelector,
  volumeTypesSelector,
} from 'app/plugins/openstack/components/storage/volume-types/selectors'
import { useVolumeBackends } from 'app/plugins/openstack/components/storage/volume-types/VolumeBackendsProvider'
import AsyncDropdown, { PropsWithAsyncDropdown } from 'core/elements/dropdown/AsyncDropdown'
import useListAction from 'core/hooks/useListAction'

export default function ClusterVMSourceType({
  disabled = false,
  name = 'Boot Source Type',
  label = 'Boot Source Type',
  value = '',
  loading = false,
  onChange,
  onNoVolumeTypes,
  ...rest
}: {
  disabled?: boolean
  name?: string
  label?: string
  value?: string
  loading?: boolean
  onChange?: (value: string) => void
  onNoVolumeTypes?: (hasNoVolumeTypes: boolean) => void
  [key: string]: any
}) {
  const { activeVolumeBackendNames, loading: loadingVolumeBackends } = useVolumeBackends()

  const { loading: loadingVolumeTypes } = useListAction(listVolumeTypes)
  const volumeTypes = useSelector(volumeTypesSelector)
  const { loading: loadingDefaultVolumeType } = useListAction(listTenantDefaultVolumeType, {
    loadingFeedback: false,
  })
  const defaultVolumeType = useSelector(defaultTenantVolumeTypesSelector)

  const options = useMemo(() => {
    if (loadingDefaultVolumeType || loadingVolumeTypes) {
      return []
    }

    const volumeTypeOptions = volumeTypes.map((volumeType) => ({
      label:
        defaultVolumeType.volume_type_id === volumeType.id
          ? `${volumeType.name || volumeType.id} (Default)`
          : volumeType.name || volumeType.id,
      value: volumeType.name || volumeType.id,
    }))

    const filterTypeOptions = volumeTypeOptions.filter((option) => {
      const backendName = volumeTypes.find((volume) => volume.name === option.value)?.extra_specs
        ?.volume_backend_name

      return activeVolumeBackendNames.includes(backendName)
    })

    return [...filterTypeOptions, { label: 'Image', value: 'image' }]
  }, [
    volumeTypes,
    defaultVolumeType,
    activeVolumeBackendNames,
    loadingVolumeTypes,
    loadingDefaultVolumeType,
  ])

  const loadingSomething = loadingDefaultVolumeType || loadingVolumeTypes || loadingVolumeBackends

  useEffect(() => {
    if (loadingSomething || !onNoVolumeTypes) return
    onNoVolumeTypes(options.length <= 1)
  }, [loadingSomething, options])

  useEffect(() => {
    if (loadingDefaultVolumeType || loadingVolumeTypes) {
      return
    }

    if (defaultVolumeType?.volume_type_id && volumeTypes.length > 0) {
      const filteredType = volumeTypes.find((type) => type.id === defaultVolumeType.volume_type_id)
      onChange((filteredType?.name || filteredType?.id) ?? '')
    } else {
      onChange(value || options[0]?.value)
    }
  }, [defaultVolumeType, volumeTypes])

  return (
    <AsyncDropdown
      {...rest}
      value={value}
      name={name}
      label={label}
      compact={false}
      disabled={disabled}
      loading={loadingSomething}
      items={options}
      onChange={onChange}
    />
  )
}
